<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import BookingsAPI from 'dashboard/api/bookings';
import ContactBookingsAPI from 'dashboard/api/contactBookings';

import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BookingFilters from 'dashboard/components/bookings/BookingFilters.vue';
import BookingCard from 'dashboard/routes/dashboard/conversation/contact/BookingCard.vue';
import ViewBookingModal from 'dashboard/routes/dashboard/conversation/contact/ViewBookingModal.vue';
import BookingFormModal from 'dashboard/routes/dashboard/conversation/contact/BookingFormModal.vue';
import {
  BOOKING_TYPES,
  STATUS_OPTIONS as BOOKING_STATUSES,
} from 'dashboard/components/bookings/bookingTypes';

const DEBOUNCE_DELAY = 300;

// The advanced filters, keyed by the API client's camelCase argument and paired
// with the snake_case URL query param they round-trip through.
const ADVANCED_FILTERS = {
  provider: 'provider',
  ratePlan: 'rate_plan',
  station: 'station',
  dateFrom: 'date_from',
  dateTo: 'date_to',
  source: 'source',
  userId: 'user_id',
  oneWay: 'one_way',
};

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const bookings = ref([]);
const totalCount = ref(0);
const perPage = ref(25);
const isFetching = ref(false);
const hasError = ref(false);

const searchValue = ref(route.query.q || '');
const bookingType = ref(route.query.booking_type || '');
const status = ref(route.query.status || '');
const currentPage = ref(Number(route.query.page) || 1);

const advancedFilters = ref({
  provider: route.query.provider || '',
  ratePlan: route.query.rate_plan || '',
  station: route.query.station || '',
  dateFrom: route.query.date_from || '',
  dateTo: route.query.date_to || '',
  source: route.query.source || '',
  userId: route.query.user_id || '',
  oneWay: route.query.one_way === 'true',
});

const filterOptions = ref({
  providers: [],
  rate_plans: [],
  agents: [],
  stations: [],
});

const activeFilterCount = computed(
  () => Object.values(advancedFilters.value).filter(Boolean).length
);

// Opens by itself when the page loads with advanced filters already in the URL,
// so a shared link shows what it is filtering by.
const isFilterBarOpen = ref(activeFilterCount.value > 0);

const selectedBooking = ref(null);
const shouldShowViewModal = ref(false);
const editingBooking = ref(null);
const shouldShowFormModal = ref(false);

const isEmpty = computed(
  () => !isFetching.value && bookings.value.length === 0
);

// The empty value is a real option rather than Select's placeholder, because
// clearing the filter has to stay selectable once a filter is applied.
const bookingTypeOptions = computed(() => [
  { value: '', label: t('BOOKINGS_PAGE.ALL_TYPES') },
  ...BOOKING_TYPES.map(type => ({
    value: type.value,
    label: t(type.labelKey),
  })),
]);

const statusOptions = computed(() => [
  { value: '', label: t('BOOKINGS_PAGE.ALL_STATUSES') },
  ...BOOKING_STATUSES.map(option => ({
    value: option.value,
    label: t(option.labelKey),
  })),
]);

const syncUrl = () => {
  const advancedQuery = {};
  Object.entries(ADVANCED_FILTERS).forEach(([name, param]) => {
    const value = advancedFilters.value[name];
    advancedQuery[param] = value ? String(value) : undefined;
  });
  router.replace({
    query: {
      page: String(currentPage.value),
      q: searchValue.value || undefined,
      booking_type: bookingType.value || undefined,
      status: status.value || undefined,
      ...advancedQuery,
    },
  });
};

const fetchBookings = async () => {
  isFetching.value = true;
  hasError.value = false;
  try {
    const { data } = await BookingsAPI.get({
      page: currentPage.value,
      bookingType: bookingType.value,
      status: status.value,
      q: searchValue.value,
      ...advancedFilters.value,
    });
    bookings.value = data.payload;
    totalCount.value = data.meta.count;
    perPage.value = data.meta.per_page;
  } catch {
    hasError.value = true;
    bookings.value = [];
    totalCount.value = 0;
  } finally {
    isFetching.value = false;
  }
};

const fetchFilterOptions = async () => {
  try {
    const { data } = await BookingsAPI.filterOptions();
    filterOptions.value = data;
  } catch {
    // A missing options list only costs the dropdowns their choices; the list
    // itself still renders, so there is nothing to tell the agent about.
  }
};

// Any filter change invalidates the current page number, so reset to page 1
// before refetching — otherwise a narrower filter can land on an empty page.
const applyFilters = () => {
  currentPage.value = 1;
  syncUrl();
  fetchBookings();
};

const onBookingTypeChange = value => {
  bookingType.value = value;
  applyFilters();
};

const onStatusChange = value => {
  status.value = value;
  applyFilters();
};

const onAdvancedFilterUpdate = changes => {
  advancedFilters.value = { ...advancedFilters.value, ...changes };
  applyFilters();
};

const onClearFilters = () => {
  advancedFilters.value = {
    provider: '',
    ratePlan: '',
    station: '',
    dateFrom: '',
    dateTo: '',
    source: '',
    userId: '',
    oneWay: false,
  };
  applyFilters();
};

const onSearch = debounce(value => {
  searchValue.value = value;
  applyFilters();
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  currentPage.value = page;
  syncUrl();
  fetchBookings();
};

const onViewBooking = booking => {
  selectedBooking.value = booking;
  shouldShowViewModal.value = true;
};

const closeViewModal = () => {
  shouldShowViewModal.value = false;
  selectedBooking.value = null;
};

const onEditBooking = booking => {
  editingBooking.value = booking;
  shouldShowFormModal.value = true;
};

const closeFormModal = () => {
  shouldShowFormModal.value = false;
  editingBooking.value = null;
};

// Delete goes through the contact-scoped endpoint (the only one that exists),
// then refetches so the account-wide list and its count stay accurate. The
// options are refetched too: the deleted row may have held the last booking
// for a provider, station or agent.
const onDeleteBooking = async bookingId => {
  const booking = bookings.value.find(item => item.id === bookingId);
  if (!booking) return;
  await ContactBookingsAPI.delete(booking.contact_id, bookingId);
  await Promise.all([fetchBookings(), fetchFilterOptions()]);
};

const openContact = contactId => {
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId },
  });
};

onMounted(() => {
  fetchBookings();
  fetchFilterOptions();
});
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="flex flex-col gap-3 py-6 mx-auto max-w-5xl">
        <div
          class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <span class="text-xl font-medium truncate text-n-slate-12">
            {{ t('BOOKINGS_PAGE.HEADER') }}
          </span>
          <div class="flex flex-wrap items-center gap-2">
            <Input
              :model-value="searchValue"
              type="search"
              :placeholder="t('BOOKINGS_PAGE.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-9 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              class="w-full sm:w-64"
              @input="onSearch($event.target.value)"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
            <Select
              :model-value="bookingType"
              :options="bookingTypeOptions"
              @update:model-value="onBookingTypeChange"
            />
            <Select
              :model-value="status"
              :options="statusOptions"
              @update:model-value="onStatusChange"
            />
            <Button
              faded
              slate
              icon="i-lucide-sliders-horizontal"
              @click="isFilterBarOpen = !isFilterBarOpen"
            >
              {{ t('BOOKINGS_PAGE.FILTERS.TOGGLE') }}
              <span
                v-if="activeFilterCount"
                class="flex items-center justify-center rounded-full size-4 bg-n-brand text-[10px] font-semibold text-white"
              >
                {{ activeFilterCount }}
              </span>
            </Button>
          </div>
        </div>

        <BookingFilters
          :filters="advancedFilters"
          :options="filterOptions"
          :is-open="isFilterBarOpen"
          @update="onAdvancedFilterUpdate"
          @clear="onClearFilters"
        />
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full py-4 mx-auto max-w-5xl">
        <div v-if="isFetching" class="flex items-center justify-center p-8">
          <Spinner />
        </div>
        <p
          v-else-if="hasError"
          class="p-8 text-base text-center text-n-ruby-11"
        >
          {{ t('BOOKINGS_PAGE.ERROR') }}
        </p>
        <p
          v-else-if="isEmpty"
          class="p-8 text-base text-center text-n-slate-11"
        >
          {{ t('BOOKINGS_PAGE.EMPTY_STATE') }}
        </p>
        <div
          v-else
          class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
        >
          <BookingCard
            v-for="booking in bookings"
            :key="booking.id"
            :booking="booking"
            :contact="booking.contact"
            @view="onViewBooking"
            @open-contact="openContact"
          />
        </div>
      </div>
    </main>

    <footer v-if="totalCount > 0" class="sticky bottom-0 z-0">
      <PaginationFooter
        current-page-info="BOOKINGS_PAGE.PAGINATION_FOOTER.SHOWING"
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="perPage"
        @update:current-page="onPageChange"
      />
    </footer>

    <ViewBookingModal
      v-model:show="shouldShowViewModal"
      :booking="selectedBooking"
      @close="closeViewModal"
      @delete="onDeleteBooking"
      @edit="onEditBooking"
    />

    <BookingFormModal
      v-model:show="shouldShowFormModal"
      :contact-id="editingBooking?.contact_id"
      :booking="editingBooking"
      @close="closeFormModal"
      @saved="fetchBookings"
    />
  </section>
</template>
